import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
    Settings, Phone, Mail, MapPin, Clock, Building2, 
    Save, Loader2, Globe, Facebook, Instagram
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { toast } from 'sonner';

export default function SettingsSection() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        company_name: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        postal_code: '',
        siret: '',
        tva: '',
        opening_hours: {
            monday_saturday: '',
            sunday: ''
        },
        social_links: {
            facebook: '',
            instagram: ''
        },
        pickup_message: ''
    });

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const data = await apiRequest('/admin/settings');
            setSettings(prev => ({
                ...prev,
                ...data,
                opening_hours: data.opening_hours || prev.opening_hours,
                social_links: data.social_links || prev.social_links
            }));
        } catch (error) {
            toast.error('Erreur lors du chargement des paramètres');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiRequest('/admin/settings', {
                method: 'PUT',
                body: JSON.stringify(settings)
            });
            toast.success('Paramètres enregistrés !');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const updateField = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const updateNestedField = (parent, field, value) => {
        setSettings(prev => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [field]: value
            }
        }));
    };

    if (loading) {
        return (
            <Card className="glass rounded-2xl">
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                        <Settings className="h-6 w-6 text-primary" />
                        Paramètres du site
                    </h2>
                    <p className="text-muted-foreground">
                        Modifiez les informations affichées sur votre site
                    </p>
                </div>
                <Button onClick={handleSave} disabled={saving} className="rounded-full">
                    {saving ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Enregistrement...
                        </>
                    ) : (
                        <>
                            <Save className="h-4 w-4 mr-2" />
                            Enregistrer
                        </>
                    )}
                </Button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Informations générales */}
                <Card className="glass rounded-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-primary" />
                            Informations générales
                        </CardTitle>
                        <CardDescription>
                            Nom de l'entreprise et coordonnées
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Nom de l'entreprise</Label>
                            <Input
                                value={settings.company_name}
                                onChange={(e) => updateField('company_name', e.target.value)}
                                placeholder="O.C.A Piscine"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                Téléphone
                            </Label>
                            <Input
                                value={settings.phone}
                                onChange={(e) => updateField('phone', e.target.value)}
                                placeholder="06 51 96 14 74"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                Email
                            </Label>
                            <Input
                                type="email"
                                value={settings.email}
                                onChange={(e) => updateField('email', e.target.value)}
                                placeholder="contact@exemple.com"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Message Point Relais</Label>
                            <Input
                                value={settings.pickup_message}
                                onChange={(e) => updateField('pickup_message', e.target.value)}
                                placeholder="Point Relais Pick Up"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Adresse */}
                <Card className="glass rounded-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <MapPin className="h-5 w-5 text-primary" />
                            Adresse
                        </CardTitle>
                        <CardDescription>
                            Adresse physique du magasin
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Adresse</Label>
                            <Input
                                value={settings.address}
                                onChange={(e) => updateField('address', e.target.value)}
                                placeholder="5 Place de la République"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Code postal</Label>
                                <Input
                                    value={settings.postal_code}
                                    onChange={(e) => updateField('postal_code', e.target.value)}
                                    placeholder="83780"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ville</Label>
                                <Input
                                    value={settings.city}
                                    onChange={(e) => updateField('city', e.target.value)}
                                    placeholder="Flayosc"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>N° SIRET</Label>
                            <Input
                                value={settings.siret}
                                onChange={(e) => updateField('siret', e.target.value)}
                                placeholder="928 454 719 00016"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>N° TVA</Label>
                            <Input
                                value={settings.tva}
                                onChange={(e) => updateField('tva', e.target.value)}
                                placeholder="FR51 928 454 719"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Horaires */}
                <Card className="glass rounded-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-primary" />
                            Horaires d'ouverture
                        </CardTitle>
                        <CardDescription>
                            Horaires affichés sur le site
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Lundi - Samedi</Label>
                            <Input
                                value={settings.opening_hours?.monday_saturday || ''}
                                onChange={(e) => updateNestedField('opening_hours', 'monday_saturday', e.target.value)}
                                placeholder="9h00 - 12h00 / 16h00 - 19h00"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Dimanche</Label>
                            <Input
                                value={settings.opening_hours?.sunday || ''}
                                onChange={(e) => updateNestedField('opening_hours', 'sunday', e.target.value)}
                                placeholder="Fermé"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Réseaux sociaux */}
                <Card className="glass rounded-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Globe className="h-5 w-5 text-primary" />
                            Réseaux sociaux
                        </CardTitle>
                        <CardDescription>
                            Liens vers vos réseaux sociaux (optionnel)
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Facebook className="h-4 w-4" />
                                Facebook
                            </Label>
                            <Input
                                value={settings.social_links?.facebook || ''}
                                onChange={(e) => updateNestedField('social_links', 'facebook', e.target.value)}
                                placeholder="https://facebook.com/votrepage"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Instagram className="h-4 w-4" />
                                Instagram
                            </Label>
                            <Input
                                value={settings.social_links?.instagram || ''}
                                onChange={(e) => updateNestedField('social_links', 'instagram', e.target.value)}
                                placeholder="https://instagram.com/votrecompte"
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Save button mobile */}
            <div className="lg:hidden">
                <Button onClick={handleSave} disabled={saving} className="w-full rounded-full">
                    {saving ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Enregistrement...
                        </>
                    ) : (
                        <>
                            <Save className="h-4 w-4 mr-2" />
                            Enregistrer les modifications
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
}
